import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import userService from "../../../store/services/user.service";
import invoiceRegistryService from "../../../store/services/invoiceRegistry.service";
import {
  Tooltip,
  TableCellLayout,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
} from "@fluentui/react-components";
import {
  CheckmarkCircleIcon,
  DismissCircleIcon,
  DeleteIcon,
  EditIcon,
  FlagIcon,
  MeatBallsMenuIcon,
  RemindIcon,
  AttachementIcon,
} from "../../../modules/default-svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { generalActions } from "../../../store/actions";
import CustomSelect from "../select/CustomSelect";
import Spin from "../spinner/Spin";
import {
  getAccountCurrency,
  dateOnlyFormatted,
  displayDecimalFormatted,
} from "../../../utils/systemSettings";
import {
  compareArrays,
  getDeletePermission,
  getEditPermission,
  sortColumnsByOrderlist,
} from "../../../helpers/utils";
import SafeHtml from "../../safeHtml/SafeHtml";
import projectService from "../../../store/services/projects.service";
import {
  createInvoiceRegistryRequest,
  updateInvoiceRegistryRequest,
  updateInvoiceRegistryFlagRequest,
} from "../../../store/actions/invoiceRegistry.actions";
import {
  isValidDate,
  localDateToISODateString,
} from "../../../utils/systemSettings";
import TableCellSafeHtml from "../../safeHtml/TableCellSafeHtml";
import { createSkeletonRows, useTableContextChange } from "./utils/tableUtils";

const EditInput = ({
  label,
  value,
  onChange,
  type,
  onBlur,
  dateFormat,
  table,
  projectId,
  dispatch,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const formatValue = (val) => {
    if (!val) return null;
    if (val.label && val.value) return val;
    return { label: val.name, value: val.id };
  };

  const handleDateChange = (newDate) => {
    if (isValidDate(newDate)) {
      const newValue = localDateToISODateString(newDate);
      setInputValue(newValue);
      onChange(newValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const creatingRowState = table.getState().creatingRow;
      if (table?.options?.onCreatingRowSave && creatingRowState) {
        const currentValues = {
          ...creatingRowState.values,
        };
        table.options.onCreatingRowSave({
          values: currentValues,
          table,
        });
      }
    }
  };

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleSelectChange = (item) => {
    if (!item) return;
    const newValue = { id: item.value, name: item.label };
    handleInputChange(newValue);
  };

  const searchHandler = (value) => {
    if (value === "") {
      setPurchaseOrders([]);
      return;
    }
    projectService
      .getProjectPurchaseOrders(projectId, value)
      .then((data) => {
        const options = data.map((option) => ({
          label: option.poId,
          value: option.id,
        }));
        setPurchaseOrders(options);
      })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title:
              "Something went wrong fetching data. Please try again later.",
            type: "error",
          })
        );
      });
  };

  if (type === "text") {
    return (
      <input
        placeholder={`Enter ${label}`}
        value={inputValue}
        type="text"
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "number") {
    return (
      <input
        placeholder={`Enter ${label}`}
        value={inputValue}
        type="number"
        min={0}
        onChange={(e) => handleInputChange(+e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "select") {
    let searchTextFunc = searchHandler;
    let options = purchaseOrders;
    let selectText = "Purchase order";

    return (
      <div className="d-flex align-items-center w-full max-w-md mx-auto">
        <CustomSelect
          searchText={searchTextFunc}
          SelectText={selectText}
          CustomClass="ModalAvatarSearch CustomSelect"
          options={options}
          value={formatValue(value)}
          onChange={handleSelectChange}
          creatable={true}
          setCreated={handleSelectChange}
          handleBlur={onBlur}
          isDatatable
          selectWd="w-100"
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <DatePicker
      selected={inputValue ? moment(inputValue).toDate() : null}
      onChange={handleDateChange}
      dateFormat={dateFormat}
      className="CustomDatePickerInput"
      onBlur={onBlur}
      popperPlacement="bottom-start"
      placeholderText={dateFormat}
      onKeyDown={handleKeyDown}
      popperClassName="CustomDatePickerPopper"
      showPopperArrow={false}
    />
  );
};

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: "#342c82",
    color: "#FFFFFF",
  },
});

const InvoiceRegistryTable = (props) => {
  const {
    columnsList,
    rowData,
    setIsCreate,
    searchHandler,
    tableName,
    handleColumnOrdering,
    dispatch,
    setPageNum,
    perPageItems,
    pagination,
    handleReminder,
    handleEdit,
    handleDelete,
    parentType,
    clientId,
    portfolioId,
    programId,
    projectId,
    loading,
    handleAddRow,
    handleCancelAddRow,
    setRowData,
    isHeader,
    triggerSearch,
  } = props;

  const styles = useStyles();
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const setting = JSON.parse(localStorage.getItem("settings"));
  const [editedRows, setEditedRows] = useState({});
  const tableContainerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const selectRefs = useRef({});
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [popoverStates, setPopoverStates] = useState({});
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { hasContextChanged } = useTableContextChange({
    contextValues: {
      clientId,
      portfolioId,
      programId,
      projectId,
      parentType
    },
    setIsFetchingMore,
    setRows,
    setRowData,
  });

  const handlePopoverChange = (itemId, isOpen) => {
    setPopoverStates(isOpen ? { [itemId]: isOpen } : {});
  };
  const [colsList, setColumnsList] = useState([]);
  useEffect(() => {
    if (columnsList.length) {
      setColumnsList(columnsList);
    }
  }, [columnsList]);
  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = cols.map((col) => ({
      name: col.Label,
      customName: col.accessorKey === columnId ? headerValue : col.CustomName,
      order: col.Order,
      isVisible: col.Visibility,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => { })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setColumnsList((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId
              ? { ...col, header: headerValue, customName: headerValue }
              : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  const handleFlag = (id, status) => {
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index !== -1) {
      newData[index].isFlagged = status;
    }

    setRowData(
      newData.sort((a, b) => {
        return b.isFlagged - a.isFlagged;
      })
    );
    dispatch(
      updateInvoiceRegistryFlagRequest({
        id: id,
        payload: [
          {
            op: "replace",
            path: "/IsFlagged",
            value: status,
          },
        ],
      })
    );
  };

  const handleToggleField = (id, field, newValue) => {
    const prevData = [...rowData];
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index === -1) return;
    newData[index] = { ...newData[index], [field]: newValue };
    setRowData(newData);

    invoiceRegistryService
      .updateInvoiceRegistryInLedgerStatus(id, newValue)
      .catch(() => {
        setRowData(prevData);
        dispatch(
          generalActions.showToast({
            title: "Failed to update Invoice. Please try again.",
            type: "error",
          })
        );
      });
  };

  const fetchMoreData = useCallback(() => {
    if (!pagination?.HasNext || loading || isFetchingMore || hasContextChanged) return;
    setIsFetchingMore(true);
    setPageNum(pagination.CurrentPage + 1);
  }, [pagination, loading, isFetchingMore, setPageNum, hasContextChanged]);

  useEffect(() => {
    if (!loading && isFetchingMore) {
      setIsFetchingMore(false);
    }
  }, [loading, rowData.length]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (!containerRefElement || loading || !pagination?.HasNext) return;

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      const scrolledToBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      const isScrollingDown = scrollTop > lastScrollTop.current;

      lastScrollTop.current = scrollTop;

      if (isScrollingDown && scrolledToBottom) {
        fetchMoreData();
      }
    },
    [fetchMoreData, loading, pagination]
  );

  const throttledFetchMore = useCallback(
    (() => {
      let timeoutId;
      return (containerRefElement) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchMoreOnBottomReached(containerRefElement);
        }, 100);
      };
    })(),
    [fetchMoreOnBottomReached]
  );

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const handleScroll = () => throttledFetchMore(container);
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [throttledFetchMore]);

  // Reset scroll position when search is triggered
  useEffect(() => {
    if (tableContainerRef.current && triggerSearch !== undefined) {
      tableContainerRef.current.scrollTop = 0;
      lastScrollTop.current = 0;
    }
  }, [triggerSearch]);

  const formatPayload = (payload) => {
    const formattedPayload = {};
    Object.keys(payload).forEach((key) => {
      formattedPayload[key] = payload[key];
    });
    return formattedPayload;
  };

  const handleSave = (rowId, payload) => {
    dispatch(
      updateInvoiceRegistryRequest({
        id: rowId,
        payload,
      })
    );
    setEditedRows({});
  };

  const handleCreate = (payload) => {
    dispatch(createInvoiceRegistryRequest({ payload, perPageItems }));
  };

  const getSortingFunction = (col) => {
    switch (col.label) {
      case "Supplier Name":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.purchaseOrder?.vendor || "").toLowerCase();
          const b = (rowB.original.purchaseOrder?.vendor || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Supplier Inv #":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.supplierInvId || "").toLowerCase();
          const b = (rowB.original.supplierInvId || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "PO":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.purchaseOrder?.name || "").toLowerCase();
          const b = (rowB.original.purchaseOrder?.name || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Payment Code":
        return (rowA, rowB, columnId) => {
          const a = (
            rowA.original.cashflowLineItem?.lineDescription || ""
          ).toLowerCase();
          const b = (
            rowB.original.cashflowLineItem?.lineDescription || ""
          ).toLowerCase();
          return a.localeCompare(b);
        };

      case "Invoice Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.date || 0);
          const b = new Date(rowB.original.date || 0);
          return a.getTime() - b.getTime();
        };

      case "Internal Inv #":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.internalInvId || "").toLowerCase();
          const b = (rowB.original.internalInvId || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Value Before Tax":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.valueBeforeTax) || 0;
          const b = parseFloat(rowB.original.valueBeforeTax) || 0;
          return a - b;
        };

      case "Paid Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.paidDate || 0);
          const b = new Date(rowB.original.paidDate || 0);
          return a.getTime() - b.getTime();
        };

      case "Account":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.account || "").toLowerCase();
          const b = (rowB.original.account || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Comments":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.comments || "")
            .toLowerCase()
            .replace(/<[^>]*>/g, ""); // Strip HTML tags
          const b = (rowB.original.comments || "")
            .toLowerCase()
            .replace(/<[^>]*>/g, "");
          return a.localeCompare(b);
        };

      case "In Ledger":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.isInLedger) || 0;
          const b = parseFloat(rowB.original.isInLedger) || 0;
          return a - b;
        };
      default:
        return "alphanumeric";
    }
  };

  const getSortDescFirst = (col) => {
    const descFirstColumns = [
      "PO",
      "Invoice Date",
      "Value Before Tax",
      "Paid Date",
    ];
    return descFirstColumns.includes(col.label);
  };

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;

    const baseRows = [...rowData];

    if (isFetchingMore && pagination?.HasNext) {
      const skeletonRows = createSkeletonRows();
      setRows([...baseRows, ...skeletonRows]);
    } else {
      setRows(baseRows);
    }
  }, [rowData, isFetchingMore, pagination?.HasNext, colsList]);

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;
    const columnsOrder = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.accessorKey);
    setColumnOrder(columnsOrder);
    const columns = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableSorting: editingHeader === col.accessorKey ? false : true,
          sortingFn: getSortingFunction(col),
          sortDescFirst: getSortDescFirst(col),
          muiEditTextFieldProps: ({ cell, row }) => ({
            sx: {
              fontFamily: "Figtree-Regular",
              fontSize: "14px",
              padding: "0.5rem",
              maxHeight: "28px",
            },
            required: col.required || false,
            error: !!validationErrors?.[`${row.id}_${cell.column.id}`],
            helperText: validationErrors?.[`${row.id}_${cell.column.id}`],
            onFocus: () =>
              setValidationErrors({
                ...validationErrors,
                [cell.column.id]: undefined,
              }),
            inputRef: (el) => {
              if (col.columnType === "select") {
                selectRefs.current[`${row.id}_${cell.column.id}`] = el;
              }
            },
            onChange: (event) => {
              const newValue = event.target.value;
              setEditedRows((prev) => ({
                ...prev,
                [row.id]: {
                  ...prev[row.id],
                  [cell.column.id]: newValue,
                },
              }));
            },
            onBlur: (event) => {
              const value = event.target.value;
              const validationError =
                col.required && !value ? "Required" : undefined;
              setValidationErrors((prev) => ({
                ...prev,
                [`${row.id}_${cell.column.id}`]: validationError,
              }));
            },
          }),
          Cell: ({ cell, row }) => {
            const item = row.original;
            // Render skeleton for skeleton rows
            if (item.isSkeleton) {
              return (
                <div
                  style={{
                    height: '20px',
                    background: 'linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    borderRadius: '4px',
                    width: '100%'
                  }}
                />
              );
            }
            switch (col.label) {
              case "Supplier Name":
                return (
                  <div className="d-flex justify-content-between align-items-center inputWidth">
                    <div className="d-flex align-items-center inputWidth">
                      <Tooltip
                        positioning="before"
                        content={{
                          children: item.isFlagged ? "Unflag" : "Flag",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span
                          className={`flag ${item.isFlagged ? "flagActive" : ""
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleFlag(item.id, !item.isFlagged);
                          }}
                        >
                          <FlagIcon />
                        </span>
                      </Tooltip>
                      <span className="d-flex w-full text-truncate cursor-pointer"
                        onClick={(e) => {
                          if (getEditPermission("PurchaseOrders")) {
                            e.stopPropagation();
                            e.preventDefault();
                            handleEdit(item);
                            handlePopoverChange(
                              item.id,
                              !popoverStates[item.id]
                            );
                          }
                        }}
                      >
                        {item.attachementsCount > 0 && (
                          <div className="mr-1">
                            <AttachementIcon count={item.attachementsCount} />
                          </div>
                        )}
                        {item.purchaseOrder.vendor}
                      </span>
                      <div className="meatballs-trigger">
                        <Popover
                          open={popoverStates[item.id]}
                          onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                          positioning="below-start"
                        >
                          <PopoverTrigger disableButtonEnhancement>
                            <TableCellLayout
                              media={<MeatBallsMenuIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            />
                          </PopoverTrigger>
                          <PopoverSurface
                            tabIndex={-1}
                            className="listingPopoverWrapper"
                          >
                            <div className="listingPopoverContent">
                              {/* {getEditPermission("PurchaseOrders") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleEdit(item);
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                  }}
                                >
                                  <EditIcon />
                                  <p className="text-sm-400 ms-12">Edit</p>
                                </div>
                              )} */}
                              <div
                                className="popItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleReminder(item);
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                }}
                              >
                                <RemindIcon />
                                <p className="text-sm-400 ms-12">Remind me</p>
                              </div>
                              {getDeletePermission("PurchaseOrders") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                    handleDelete(item);
                                  }}
                                >
                                  <DeleteIcon />
                                  <p className="text-sm-400 ms-12">Delete</p>
                                </div>
                              )}
                            </div>
                          </PopoverSurface>
                        </Popover>
                      </div>
                    </div>
                  </div>
                );
              case "Supplier Inv #":
                return item.supplierInvId;
              case "PO":
                return item.purchaseOrder.name;
              case "Payment Code":
                return item.cashflowLineItem.lineDescription;
              case "Invoice Date":
                return item.date
                  ? dateOnlyFormatted(setting, item.date)
                  : "";
              case "Internal Inv #":
                return item.internalInvId ?? "";
              case "Value Before Tax":
                return `${getAccountCurrency(
                  setting
                )} ${displayDecimalFormatted(setting, item.valueBeforeTax)}`;
              case "Paid Date":
                return item.paidDate
                  ? dateOnlyFormatted(setting, item.paidDate)
                  : "";
              case "Account":
                return <div className="w-[140px]">{item.account}</div>;
              case "Comments":
                return <TableCellSafeHtml html={item.comments} />;
              case "In Ledger":
                return (
                  <div
                    className="flex w-100 justify-center items-center cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleField(item.id, "isInLedger", !item.isInLedger);
                    }}
                  >
                    {item.isInLedger ? (
                      <CheckmarkCircleIcon />
                    ) : (
                      <DismissCircleIcon />
                    )}
                  </div>
                );
              default:
                return cell.getValue();
            }
          },
          Edit: ({ cell, row, table }) => {
            const value =
              editedRows[row.id]?.[cell.column.id] ??
              cell.getValue() ??
              row.original[cell.column.id] ??
              (col.columnType === "select" ? null : "");
            return (
              <EditInput
                label={col.label}
                value={value}
                onChange={(newValue) => {
                  if (row.id.startsWith("mrt-row-create")) {
                    table.setCreatingRow((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        [cell.column.id]: newValue,
                      },
                    }));
                  } else {
                    setEditedRows((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        [cell.column.id]: newValue,
                      },
                    }));
                  }
                }}
                onBlur={() => {
                  if (!row.id.startsWith("mrt-row-create")) {
                    const newValue = editedRows[row.id]?.[cell.column.id];
                    table.options.onEditingCellSave?.({
                      cell,
                      value: newValue,
                      row,
                      table,
                    });
                    table.setEditingCell(null);
                  }
                }}
                type={col.columnType}
                dateFormat={
                  col.columnType === "date"
                    ? setting.regionalSettings.dateFormat
                    : null
                }
                table={table}
                projectId={projectId}
                dispatch={dispatch}
              />
            );
          },
          Header: ({ column }) => {
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <span>{column.columnDef.header}</span>
            );
          },
        };
      });
    setCols((prevCols) => {
      if (JSON.stringify(prevCols) !== JSON.stringify(columns)) {
        return columns;
      }
      return prevCols;
    });

    const uniqueRows =
      rowData?.filter(
        (row, index, self) => index === self.findIndex((r) => r.id === row.id)
      ) || [];
    setRows((prevRows) => {
      if (JSON.stringify(prevRows) !== JSON.stringify(uniqueRows)) {
        return uniqueRows;
      }
      return prevRows;
    });
  }, [colsList, rowData, editingHeader, headerValue]);

  const table = useMaterialReactTable({
    columns: cols,
    data: rows,
    enableHiding: true,
    enableSorting: true,
    manualSorting: false,
    projectId: projectId,
    parentType: parentType,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableCreatingRow: true,
    createDisplayMode: "row",
    positionCreatingRow: "bottom",
    initialState: {
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
      columnOrder: cols.map((col) => col.order),
    },
    state: {
      sorting,
      isLoading: rows.length === 0 && loading,
      columnSizing,
      columnOrder,
      showLoadingOverlay: false,
      showProgressBars: false,
    },
    onSortingChange: setSorting,
    onCreatingRowCancel: () => {
      setValidationErrors({});
      handleCancelAddRow();
    },
    onCreatingRowSave: ({ values, table }) => {
      if (!values.purchaseOrder?.trim()) {
        dispatch(
          generalActions.showToast({
            title: "The Purchase Order field is required",
            type: "error",
          })
        );
        return;
      }

      const payload = {};
      handleCreate(payload);
      setValidationErrors({});
      table.setCreatingRow(null);
    },

    getCreateRowInitialValues: () => ({
      ProjectId: projectId,
    }),
    onEditingRowCancel: () => {
      setValidationErrors({});
      setEditedRows({});
    },
    positionGlobalFilter: "left",
    onEditingCellSave: ({ cell, row }) => {
      const editedRow = editedRows[row.id];
      const newValue =
        editedRow?.[cell.column.id] !== undefined
          ? editedRow[cell.column.id]
          : cell.getValue();

      if (newValue === cell.getValue()) return; // Skip if no real change
      setRows((prevRows) =>
        prevRows.map((r, index) => {
          if (index === row.index) {
            return { ...r, [cell.column.id]: newValue };
          }
          return r;
        })
      );

      // Remove the specific cell edit
      setEditedRows((prev) => {
        const updated = { ...prev };
        if (updated[row.id]) {
          delete updated[row.id][cell.column.id];
          if (Object.keys(updated[row.id]).length === 0) {
            delete updated[row.id];
          }
        }
        return updated;
      });

      const prePayload = {
        ...row.original,
        [cell.column.id]: newValue,
        ProjectId: row.original.project.id,
      };
      const payload = formatPayload(prePayload);
      handleSave(row.original.id, payload);
    },
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === "function" ? updater() : updater;
      const neworderList = newOrder
        .filter((item) => item !== "mrt-row-spacer")
        .filter(Boolean);
      const areEqual = compareArrays(neworderList, columnOrder);
      if (areEqual) {
        return newOrder;
      }

      const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
      handleColumnOrdering(sortedColumns);
      setColumnOrder(neworderList);
      return neworderList;
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f5f5f5",
        height: "40px",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
        },
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    renderTopToolbar: () => (
      <ListingHeader
        isHeader={isHeader}
        rightPane={true}
        parentType={parentType}
        type="InvoiceRegistry"
        btnLable="Add Invoice"
        searchedData={searchHandler}
        setIsCreate={() => setIsCreate(true)}
        projectId={projectId}
        isCreatable={false}
        isSearchDisable={cols.length}
        handleCreateFunc={() => {
          handleAddRow();
          table.setCreatingRow(true);
        }}
        triggerSearch={triggerSearch}
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <div className="bottom-toolbar-container">
        <div className="toolbar-content">
          {loading || isFetchingMore ? (
            <div className="loading-indicator">
              <span>{`${!isFetchingMore ? 'Loading...' : 'Loading more data...'}`}</span>
            </div>
          ) : pagination?.TotalCount > 0 ? (
            <div className="data-info">
              <span className="data-text">
                Showing <strong>{rows.length}</strong> of{" "}
                <strong>{pagination?.TotalCount || 0}</strong> total records
              </span>
            </div>
          ) : (
            <div className="data-info">
              <span className="data-text">No records Found</span>
            </div>
          )}
        </div>
      </div>
    ),
    muiBottomToolbarProps: {
      sx: {
        position: "absolute",
        bottom: 0,
        backgroundColor: "#f5f5f5",
        padding: "0.7rem 1rem",
        width: "100%",
        height: "38px",
      },
    },
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        marginBottom: "66px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: "32px",
        position: "relative",
        "&:hover": {
          backgroundColor: row.original.isSkeleton ? "transparent" : "#ece8fd15",
        },
        cursor: row.original.isSkeleton ? "default" : "pointer",
      },
      className: row.original.isSkeleton ? "" : "hoverable-row",
    }),
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight || 115
          }px)`,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#FFFFFF",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#342c82",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: "#342c82",
          },
        },
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiSkeletonProps: {
      height: 34,
      sx: {
        background:
          "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        "@keyframes shimmer": {
          "0%": {
            backgroundPosition: "200% 0",
          },
          "100%": {
            backgroundPosition: "-200% 0",
          },
        },
      },
    },
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0.5rem 1rem",
        position: "relative",
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          fontFamily: "Figtree-Medium",
          fontWeight: "bold",
        },
      },
    }),
    renderEmptyRowsFallback: () => {
      return loading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
  });

  return <MaterialReactTable table={table} />;
};

export default InvoiceRegistryTable;
